import type { IChallenge } from "@bbj-types/challenge";
import type { ISocialOffer } from "@bbj-types/offer";
import { useAuthorizer } from "@navigations/Authorizer";
import type { ISocialOfferWithFavoriteChallenges } from "@root/api/services/offers/socialOffer/types";
import { useMemo } from "react";
import { formatConcernedChallenges } from "./utils";

export const isSocialOfferWithFavorite = (
  socialOffer: ISocialOffer
): socialOffer is ISocialOfferWithFavoriteChallenges => {
  return "favoriteChallenges" in socialOffer;
};

export type TOnUpdateOfferWithFavorite = (offer: ISocialOfferWithFavoriteChallenges) => void;
export const isOnUpdateOfferWithFavorite = (
  _offer: ISocialOfferWithFavoriteChallenges,
  onUpdateOffer: unknown
): onUpdateOffer is TOnUpdateOfferWithFavorite => {
  return onUpdateOffer !== undefined;
};

export const useCardSocialOffer = (
  offer: Pick<ISocialOffer, "address" | "tags" | "metadata" | "provider">,
  challenges: Pick<IChallenge, "name" | "tags">[]
) => {
  const isPartnerAuthorized = useAuthorizer(["partner.dashboard.visualize"]);
  const isAuthorizedActionV2 = useAuthorizer(["candidate.socialboard.actions.v2"]);
  const canOpenDetails = useMemo(
    () => isPartnerAuthorized && offer.provider?.detailsAccess,
    [isPartnerAuthorized, offer.provider?.detailsAccess]
  );

  const miscellaneousInformations = useMemo(() => {
    const concernedChallenges = formatConcernedChallenges(offer, challenges);
    const address = offer.address?.city ? offer.address?.city : offer.address?.formatted;
    let informationString = "";

    if (concernedChallenges && address) {
      informationString = `${concernedChallenges} • ${address}`;
    } else if (concernedChallenges) {
      informationString = concernedChallenges;
    } else if (address) {
      informationString = address;
    }
    if (offer.metadata?.travel?.distance) {
      informationString += ` • À ${Math.round(offer.metadata?.travel.distance / 1000)} km`;
    }

    return informationString;
  }, [offer, challenges]);

  return { miscellaneousInformations, isAuthorizedActionV2, canOpenDetails };
};
